// =============================================================================
// components/recurring/SuggestionsDrawer.jsx — Phase 82.12
//
// Right-side drawer that surfaces backend-detected recurring suggestions
// inside the new /recurring hub. Replaces the legacy inline
// `RecurringSuggestionsPanel` (which lived at the top of pages/Recurring.js)
// with a focused, on-demand surface anchored to a "Detected (N)" pill in
// the hub header.
//
// Actions supported (matches current backend surface):
//   • Accept  → POST /api/recurring/suggestions/{id}/accept
//   • Dismiss → POST /api/recurring/suggestions/{id}/dismiss
//   • (Snooze intentionally NOT wired — backend does not support it yet.)
//
// Also exposes a manual "Scan now" that re-runs detection — useful right
// after a statement import.
//
// State ownership: this component owns its own list of suggestions and a
// pending-action map. The parent only supplies open/onOpenChange and an
// `onAccepted()` callback so it can re-fetch the commitment list when a
// suggestion is promoted to a real recurring_expenses row.
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sparkles, Check, X, RefreshCw, Loader2,
  Briefcase, Wallet, TrendingUp, CreditCard, ArrowLeftRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { invalidateBriefing } from '@/lib/briefingCache';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/format';
import {
  listSuggestions, scanForSuggestions,
  acceptSuggestion, dismissSuggestion,
} from '@/lib/recurringSuggestions';

const KIND_META = {
  income:       { label: 'Income',       Icon: Briefcase     },
  expense:      { label: 'Expense',      Icon: Wallet        },
  investment:   { label: 'Investment',   Icon: TrendingUp    },
  debt_payment: { label: 'Debt payment', Icon: CreditCard    },
  transfer:     { label: 'Transfer',     Icon: ArrowLeftRight },
};

function frequencyLabel(freq) {
  if (!freq) return '';
  return freq.charAt(0).toUpperCase() + freq.slice(1);
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

// Lightweight helper exposed to the parent so it can display a "Detected
// (N)" pill without mounting the drawer. Fetches count-only; failures are
// silent (returns 0).
export function useSuggestionCount(refreshKey = 0) {
  const [count, setCount]     = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listSuggestions();
        if (cancelled) return;
        setCount((res.suggestions || []).length);
      } catch {
        if (!cancelled) setCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { count, loading };
}

export function SuggestionsDrawer({
  open,
  onOpenChange,
  currency = 'INR',
  onAccepted,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [lastScanAt, setLastScanAt]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [scanning, setScanning]       = useState(false);
  const [pending, setPending]         = useState({});   // id → 'accept' | 'dismiss'
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSuggestions();
      if (!mountedRef.current) return;
      setSuggestions(res.suggestions || []);
      setLastScanAt(res.last_scan_at || null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Re-fetch every time the drawer opens so counts stay live.
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const runScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const res = await scanForSuggestions();
      if (!mountedRef.current) return;
      setSuggestions(res.suggestions || []);
      setLastScanAt(res.last_scan_at || null);
      const n = (res.suggestions || []).length;
      toast.success(n === 0 ? 'No new recurring patterns found.' : `Found ${n} likely recurring item${n === 1 ? '' : 's'}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not scan right now.');
    } finally {
      if (mountedRef.current) setScanning(false);
    }
  };

  const accept = async (s) => {
    setPending((p) => ({ ...p, [s.id]: 'accept' }));
    const prev = suggestions;
    // Optimistic remove so the row vanishes instantly.
    setSuggestions((cur) => cur.filter((x) => x.id !== s.id));
    try {
      await acceptSuggestion(s.id);
      // Phase 84.5 — a newly-accepted suggestion becomes a real
      // recurring rule; invalidate the briefing cache so Overview NBAs
      // recompute against the fresh commitment set.
      invalidateBriefing();
      toast.success(`Added ${s.display_name} to recurring.`);
      onAccepted?.();
    } catch (e) {
      setSuggestions(prev);
      toast.error(e?.response?.data?.detail || e?.message || 'Could not add.');
    } finally {
      if (mountedRef.current) {
        setPending((p) => {
          const n = { ...p };
          delete n[s.id];
          return n;
        });
      }
    }
  };

  const dismiss = async (s) => {
    setPending((p) => ({ ...p, [s.id]: 'dismiss' }));
    const prev = suggestions;
    setSuggestions((cur) => cur.filter((x) => x.id !== s.id));
    try {
      await dismissSuggestion(s.id);
    } catch (e) {
      setSuggestions(prev);
      toast.error(e?.response?.data?.detail || e?.message || 'Could not dismiss.');
    } finally {
      if (mountedRef.current) {
        setPending((p) => {
          const n = { ...p };
          delete n[s.id];
          return n;
        });
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="recurring-suggestions-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            Detected recurring patterns
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              We noticed these payments repeating in your transactions. Add the ones that match — we never add anything automatically.
              {lastScanAt && <> <span className="opacity-70">· Last scan {timeAgo(lastScanAt)}</span></>}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={runScan}
              disabled={scanning}
              className="rounded-lg text-xs shrink-0"
              data-testid="recurring-suggestions-scan"
            >
              {scanning
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Scan
            </Button>
          </div>

          {loading ? (
            <div className="space-y-2" data-testid="recurring-suggestions-loading">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 py-8 px-4 text-center">
              <p className="text-sm text-charcoal">Nothing new to review.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Import statements or add transactions and we&apos;ll surface patterns here.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {suggestions.map((s) => {
                const meta = KIND_META[s.kind] || KIND_META.expense;
                const KIcon = meta.Icon;
                const busy = pending[s.id];
                return (
                  <div
                    key={s.id}
                    className="flex items-start justify-between gap-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
                    data-testid="recurring-suggestion-row"
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="h-8 w-8 rounded-lg grid place-items-center bg-muted text-foreground shrink-0">
                        <KIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate flex items-center gap-2 flex-wrap">
                          <span className="truncate">{s.display_name}</span>
                          <Badge variant="outline" className="rounded-full text-[10px] px-1.5">{meta.label}</Badge>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {formatMoney(Number(s.amount), currency, { compact: true })}
                          {' · '}
                          {frequencyLabel(s.frequency)}
                          {' · '}
                          {s.occurrences} seen
                          {s.last_seen_date && <> · last {s.last_seen_date}</>}
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <Button
                            size="sm"
                            onClick={() => accept(s)}
                            disabled={!!busy}
                            className="rounded-lg h-7 text-xs px-2.5"
                            data-testid="recurring-suggestion-accept"
                          >
                            {busy === 'accept'
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <><Check className="h-3.5 w-3.5 mr-1" />Add</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => dismiss(s)}
                            disabled={!!busy}
                            className="rounded-lg h-7 text-xs px-2.5 text-muted-foreground hover:text-foreground"
                            data-testid="recurring-suggestion-dismiss"
                          >
                            {busy === 'dismiss'
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <><X className="h-3.5 w-3.5 mr-1" />Dismiss</>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default SuggestionsDrawer;
